/**
 * Poll a run to a terminal state, or say plainly that it gave up — design D4,
 * `review-surface-integrity`.
 *
 * The extraction and resolution run banners used to poll for a fixed 30 seconds (60
 * attempts x 500ms), then give up silently and reload whatever list depended on the
 * run. A real tier-1 pass exceeds that routinely — model load plus inference over
 * 852 blocks alone took minutes — so the banner would freeze at whatever the last
 * poll saw (often "0 entities · 0/1 shards") while the run went on to succeed
 * server-side, with nothing on screen distinguishing a frozen number from a final
 * one. This backs off toward a slower cadence and polls for up to ten minutes
 * (comfortably past the measured 852-block run) before giving up — and every
 * non-terminal poll is reported to the caller with its own freshness, so "still
 * running, last checked HH:MM:SS" is always distinguishable from a final count.
 */

export const DEFAULT_POLL_CEILING_MS = 10 * 60 * 1000;

/** Test-only escape hatch: a page can set this global (via an init script, before
 * any application code runs) to shrink the ceiling so the "gave up" path is
 * reachable without an actual ten-minute wait. Production code never sets it, and
 * absent it this always resolves to `DEFAULT_POLL_CEILING_MS`. */
declare global {
  interface Window {
    __SOULSEARCH_POLL_CEILING_MS__?: number;
  }
}

export function pollCeilingMs(): number {
  const override = typeof window !== "undefined" ? window.__SOULSEARCH_POLL_CEILING_MS__ : undefined;
  return typeof override === "number" && override > 0 ? override : DEFAULT_POLL_CEILING_MS;
}

export interface PollOutcome<State> {
  state: State;
  terminal: boolean;
  checkedAt: Date;
}

export interface PollResult {
  /** `true` only when the ceiling was reached without ever seeing a terminal state
   * — never set merely because a single poll was slow. */
  gaveUp: boolean;
}

/**
 * Poll `fetchState` until `isTerminal` accepts its result or `ceilingMs` elapses.
 * `onUpdate` fires after every poll, terminal or not — the caller decides how to
 * render an in-progress state; this function only decides when to stop asking.
 */
export async function pollToTerminal<State>(
  fetchState: () => Promise<State>,
  isTerminal: (state: State) => boolean,
  onUpdate: (outcome: PollOutcome<State>) => void,
  opts: { ceilingMs?: number; minDelayMs?: number; maxDelayMs?: number; stepMs?: number } = {},
): Promise<PollResult> {
  const ceilingMs = opts.ceilingMs ?? pollCeilingMs();
  const minDelayMs = opts.minDelayMs ?? 500;
  const maxDelayMs = opts.maxDelayMs ?? 5000;
  const stepMs = opts.stepMs ?? 500;
  const startedAt = Date.now();
  let delay = minDelayMs;

  for (;;) {
    const state = await fetchState();
    const terminal = isTerminal(state);
    onUpdate({ state, terminal, checkedAt: new Date() });
    if (terminal) return { gaveUp: false };
    if (Date.now() - startedAt >= ceilingMs) return { gaveUp: true };
    await new Promise((resolve) => setTimeout(resolve, delay));
    delay = Math.min(maxDelayMs, delay + stepMs);
  }
}
